import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { lookupChainOfCustody } from "@/lib/chain-of-custody.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/_authenticated/app/compliance/trace")({ component: TracePage });

function TracePage() {
  const { tenantId } = useScope();
  const lookupFn = useServerFn(lookupChainOfCustody);
  const [q, setQ] = useState("");
  const m = useMutation({
    mutationFn: (query: string) => lookupFn({ data: { tenantId: tenantId!, query } }),
  });

  return (
    <>
      <PageHeader title="Chain of Custody" description="Enter a serial number or lot code to see every touch." />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        <form className="flex gap-2 max-w-xl" onSubmit={(e) => { e.preventDefault(); if (q.trim()) m.mutate(q.trim()); }}>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Serial number or lot code…" autoFocus />
          <Button type="submit" disabled={m.isPending || !q.trim()}>Look up</Button>
        </form>
        {m.data && (
          <div className="space-y-4">
            <div className="border rounded-md bg-card p-4">
              <div className="text-xs text-muted-foreground uppercase">{m.data.header.kind}</div>
              <pre className="text-xs mt-2 overflow-x-auto">{JSON.stringify(m.data.header, null, 2)}</pre>
            </div>
            <div className="border rounded-md bg-card">
              <div className="p-3 border-b text-sm font-medium">Timeline ({m.data.events.length})</div>
              <ol className="divide-y">
                {m.data.events.map((e, i) => (
                  <li key={i} className="px-3 py-2 flex items-start gap-3 text-sm">
                    <span className="text-xs text-muted-foreground tabular-nums w-44 shrink-0">{new Date(e.at).toLocaleString()}</span>
                    <Badge variant="outline" className="shrink-0">{e.source}</Badge>
                    <div className="flex-1">
                      <div>{e.label}</div>
                      {e.detail && <div className="text-xs text-muted-foreground">{e.detail}</div>}
                    </div>
                  </li>
                ))}
                {m.data.events.length === 0 && <li className="px-3 py-6 text-center text-muted-foreground text-sm">No events found.</li>}
              </ol>
            </div>
          </div>
        )}
        {m.error && <div className="text-sm text-destructive">{(m.error as Error).message}</div>}
      </div>
    </>
  );
}